import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PROMOTIONS_DISPLAY_LIMIT } from 'pages/Promotions/PromoConstant';
import { SelectedData, SelectOptions } from 'types/common/FormTypes';
import { PageDetails } from 'types/common/Table';
import { PromotionSearchFilters } from 'types/promotion/promotionTypes';

interface PromotionState {
  countSubmit: number;
  showAllValidateErrors: boolean;
  isEdit: boolean;
  error: string;
  success: string;
  modalBackdrop: boolean;
  loading: boolean;
  errorMsg: boolean;
  alertMessage: string;
  BranchOptions: SelectedData[];
  selectMultiBranches: SelectedData[];
  ProductCategoryOptions: SelectOptions[];
  selectMultiCategories: SelectedData[];
  selectMultiProducts: SelectedData[];
  selectMultiFreePromoCategories: SelectedData[];
  selectMultiFreePromoProducts: SelectedData[];
  PageFilters: PromotionSearchFilters;
  PageDetails: PageDetails;
}

export const initialState: PromotionState = {
  countSubmit: 0,
  showAllValidateErrors: false,
  isEdit: false,
  error: '',
  success: '',
  modalBackdrop: false,
  loading: false,
  errorMsg: false,
  alertMessage: '',
  BranchOptions: [],
  selectMultiBranches: [],
  ProductCategoryOptions: [],
  selectMultiCategories: [],
  selectMultiProducts: [],
  selectMultiFreePromoCategories: [],
  selectMultiFreePromoProducts: [],
  PageFilters: {
    promotionName: '',
    promoType: '',
    promoStatus: '',
  },
  PageDetails: {
    entriesPerPage: PROMOTIONS_DISPLAY_LIMIT,
    currentPage: 0,
    inputPageValue: 1,
  },
};

const PromotionsSlice = createSlice({
  name: 'Promotions',
  initialState,
  reducers: {
    setIsEdit: (state, action) => {
      state.isEdit = action.payload;
    },

    apiError(state, action) {
      state.error = action.payload.message;
      state.loading = true;
      state.errorMsg = true;
    },
    apiSuccess(state, action) {
      state.error = '';
      state.success = action.payload.message;
      state.modalBackdrop = true;
      state.loading = false;
      state.errorMsg = false;
      state.showAllValidateErrors = false;
    },
    setAlertMessage: (state, action: PayloadAction<string>) => {
      state.alertMessage = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setSuccess: (state, action: PayloadAction<string>) => {
      state.success = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    resetPromotion: (state) => {
      state.countSubmit = 0;
      state.showAllValidateErrors = false;
      state.isEdit = false;
      state.error = '';
      state.success = '';
      state.modalBackdrop = false;
      state.loading = false;
      state.errorMsg = false;
      state.alertMessage = '';
      state.selectMultiBranches = [];
      state.selectMultiCategories = [];
      state.selectMultiProducts = [];
      state.selectMultiFreePromoCategories = [];
      state.selectMultiFreePromoProducts = [];
    },

    setShowAllValidateErrors: (state, action) => {
      state.showAllValidateErrors = action.payload;
    },
    setModalBackdrop: (state, action) => {
      state.modalBackdrop = action.payload;
    },
    setCountSubmit: (state) => {
      state.countSubmit += 1;
    },
    setBranchOptions: (state, action) => {
      state.BranchOptions = action.payload;
    },
    setselectMultiBranches: (state, action) => {
      state.selectMultiBranches = action.payload;
    },
    setProductCategoryOptions: (state, action) => {
      state.ProductCategoryOptions = action.payload;
    },
    setselectMultiCategories: (state, action) => {
      state.selectMultiCategories = action.payload;
    },
    setselectMultiProducts: (state, action) => {
      state.selectMultiProducts = action.payload;
    },
    setselectMultiFreePromoCategories: (state, action) => {
      state.selectMultiFreePromoCategories = action.payload;
    },
    setselectMultiFreePromoProducts: (state, action) => {
      state.selectMultiFreePromoProducts = action.payload;
    },
    setPageFilters: (state, action) => {
      state.PageFilters = action.payload;
    },
    clearPageFilters: (state) => {
      state.PageFilters = {
        promotionName: '',
        promoType: '',
        promoStatus: '',
      };
    },
    setEntriesPerPage: (state, action) => {
      state.PageDetails.entriesPerPage = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.PageDetails.currentPage = action.payload;
      state.PageDetails.inputPageValue = action.payload + 1;
    },
    setInputPageValue: (state, action) => {
      state.PageDetails.inputPageValue = action.payload;
    },
  },
});

export const {
  setIsEdit,
  apiError,
  apiSuccess,
  setAlertMessage,
  setError,
  setSuccess,
  setLoading,
  resetPromotion,
  setShowAllValidateErrors,
  setModalBackdrop,
  setCountSubmit,
  setBranchOptions,
  setselectMultiBranches,
  setProductCategoryOptions,
  setselectMultiCategories,
  setselectMultiProducts,
  setselectMultiFreePromoCategories,
  setselectMultiFreePromoProducts,
  setPageFilters,
  clearPageFilters,
  setEntriesPerPage,
  setCurrentPage,
  setInputPageValue,
} = PromotionsSlice.actions;

export default PromotionsSlice.reducer;
